/**
 * Buddy's clarifying questions (R6, R54; board O „Pytania doprecyzowujące", board M's flow).
 *
 * Each question is a native radio group, so it has one tab stop and the arrows walk its options.
 * The picks are the conversation's, not the card's: a message sent from the composer while the card
 * is open answers the questions left without a pick in the trainer's own words, and carries the
 * picks with it. „Odpowiedz" stays focusable while it refuses, and names why.
 *
 * Once a later turn follows, the card goes and Buddy's sentence above it stays: the answers are in
 * the trainer's bundle below.
 */

import clsx from 'clsx';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { everyQuestionPicked, type AiClarifyingQuestion, type QuestionPicks } from '~/entities/ai-conversation';
import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';

import { ModelText } from './Messages';
import surfaces from './surfaces.module.css';
import styles from './QuestionsCard.module.css';

interface QuestionsCardProps {
  /** Buddy's sentence over the card; `null` when the turn wrote none. */
  readonly intro: string | null;
  readonly questions: readonly AiClarifyingQuestion[];
  readonly open: boolean;
  readonly picks: QuestionPicks;
  readonly onPick: (questionId: string, optionId: string) => void;
  readonly onAnswer: () => void;
  readonly onSkip: () => void;
}

export function QuestionsCard({ intro, questions, open, picks, onPick, onAnswer, onSkip }: QuestionsCardProps) {
  const { t } = useTranslation();
  const group = useId();
  const reason = useId();
  const ready = everyQuestionPicked(questions, picks);

  return (
    <>
      {intro !== null && <ModelText text={intro} />}
      {open && (
        <div className={clsx(surfaces.card, surfaces.riseCard, styles.card)}>
          {questions.map((question) => (
            <ChoiceQuestion
              key={question.id}
              question={question}
              name={`${group}-${question.id}`}
              picked={picks[question.id] ?? null}
              onPick={(optionId) => {
                onPick(question.id, optionId);
              }}
            />
          ))}
          <div className={styles.foot}>
            <span id={reason} className={styles.hint}>
              {t('web:aiPlan.questions.hint')}
            </span>
            <button type="button" className={clsx(surfaces.button, surfaces.ghost)} onClick={onSkip}>
              {t('web:aiPlan.questions.skip')}
            </button>
            <button
              type="button"
              className={clsx(surfaces.button, surfaces.primary)}
              aria-disabled={ready ? undefined : 'true'}
              aria-describedby={ready ? undefined : reason}
              onClick={() => {
                if (ready) onAnswer();
              }}
            >
              {t('web:aiPlan.questions.answer')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

interface ChoiceQuestionProps {
  readonly question: AiClarifyingQuestion;
  /** The radio group's name, unique on the page. */
  readonly name: string;
  readonly picked: string | null;
  readonly onPick: (optionId: string) => void;
  /** The legend's id, for a control the question explains. */
  readonly legendId?: string;
}

/** One question as a native radio group: one tab stop, the arrows walk its options. */
export function ChoiceQuestion({ question, name, picked, onPick, legendId }: ChoiceQuestionProps) {
  return (
    <fieldset className={styles.question}>
      <legend id={legendId} className={styles.title}>
        {question.text}
      </legend>
      <div className={styles.options}>
        {question.options.map((option) => {
          const chosen = picked === option.id;
          return (
            <label key={option.id} className={clsx(surfaces.option, chosen && surfaces.optionOn)}>
              <input
                className={hiddenControl.control}
                type="radio"
                name={name}
                checked={chosen}
                onChange={() => {
                  onPick(option.id);
                }}
              />
              {option.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
